import React, { useState, useEffect, useCallback, useMemo } from 'react';
import Tooltip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';
import { useTranslation } from 'react-i18next';
import excelExportIcon from '../images/excelexport.png';
import { useWebSocket } from './WebSocketContext';
import CircularProgress from '@mui/material/CircularProgress';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import Slide from '@mui/material/Slide';
import Typography from '@mui/material/Typography';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import TextField from '@mui/material/TextField';
import Stack from '@mui/material/Stack';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Box from '@mui/material/Box';

const STANDARD_EXPORT_COLUMNS = new Set([
  'time',
  'summary',
  'source',
  'eventtime',
  'eventsource',
  'event_summary',
  'event_time',
]);

const CORE_EXPORT_COLUMNS = ['time', 'source'];

const getCoreColumnLabel = (column, t) => {
  switch (column) {
    case 'time':
      return t('export_to_excel.column_time', 'Time');
    case 'source':
      return t('export_to_excel.column_source', 'Source');
    default:
      return column;
  }
};

const normalizeTagName = (tag) => {
  if (typeof tag === 'string') {
    return tag.trim();
  }

  if (tag && typeof tag === 'object') {
    return String(tag.variable || tag.name || tag.label || '').trim();
  }

  return '';
};

const extractAvailableTags = (rows) => {
  if (!Array.isArray(rows) || rows.length === 0) {
    return [];
  }

  const tags = new Map();

  rows.forEach((row) => {
    if (!row || typeof row !== 'object') {
      return;
    }

    const customFields = Array.isArray(row.customFields) ? row.customFields : [];
    if (customFields.length > 0) {
      customFields.forEach((field) => {
        const variable = normalizeTagName(field);
        if (!variable || STANDARD_EXPORT_COLUMNS.has(variable) || tags.has(variable)) {
          return;
        }

        tags.set(variable, {
          variable,
          label: String(field?.description || field?.label || variable).trim() || variable,
        });
      });
      return;
    }

    Object.keys(row).forEach((key) => {
      if (STANDARD_EXPORT_COLUMNS.has(key) || key === 'customFields') {
        return;
      }

      const value = row[key];
      if (value && typeof value === 'object') {
        return;
      }

      if (!tags.has(key)) {
        tags.set(key, { variable: key, label: key });
      }
    });
  });

  return Array.from(tags.values()).sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }));
};

const ExportToExcel = ({ data, fileName, caseid }) => {
  const { t } = useTranslation();
  const { ws, subscribeToMessages } = useWebSocket();
  const [isExporting, setIsExporting] = useState(false);
  const [exportStatus, setExportStatus] = useState({ open: false, message: '', severity: 'info' });
  const [tagDialogOpen, setTagDialogOpen] = useState(false);
  const [selectedTags, setSelectedTags] = useState([]);
  const [tagFilter, setTagFilter] = useState('');
  const [selectionTouched, setSelectionTouched] = useState(false);
  
  const primaryColor = '#5757b0';
  const lightPurple = '#f0f0ff';
  const availableTags = useMemo(() => {
    const coreColumns = CORE_EXPORT_COLUMNS.map((variable) => ({
      variable,
      label: getCoreColumnLabel(variable, t),
      isCore: true,
    }));
    const customColumns = extractAvailableTags(data).map((tag) => ({
      ...tag,
      isCore: false,
    }));

    return [...coreColumns, ...customColumns];
  }, [data, t]);
  const availableTagNames = useMemo(() => availableTags.map((tag) => tag.variable), [availableTags]);
  const availableTagSet = useMemo(() => new Set(availableTagNames), [availableTagNames]);
  const filteredTags = useMemo(() => {
    const normalizedFilter = tagFilter.trim().toLowerCase();

    if (!normalizedFilter) {
      return availableTags;
    }

    return availableTags.filter((tag) => {
      return tag.variable.toLowerCase().includes(normalizedFilter)
        || tag.label.toLowerCase().includes(normalizedFilter);
    });
  }, [availableTags, tagFilter]);

  useEffect(() => {
    if (!selectionTouched && availableTagNames.length > 0) {
      setSelectedTags(availableTagNames);
      return;
    }

    setSelectedTags((current) => {
      if (!current.length) {
        return current;
      }

      const nextSelectedTags = current.filter((tag) => availableTagSet.has(tag));
      return nextSelectedTags.length === current.length ? current : nextSelectedTags;
    });
  }, [availableTagNames, availableTagSet, selectionTouched]);

  // Function to handle WebSocket messages related to export
  useEffect(() => {
    const unsubscribe = subscribeToMessages((parsedMessage) => {
      // Message is already parsed in WebSocketContext
      // Only handle regular exportexcel command, not variable exports
      if (parsedMessage.command === 'exportexcel') {
        setIsExporting(false);
        
        if (parsedMessage.data && parsedMessage.data.status === 'success' && parsedMessage.data.data) {
          // Handle successful export with base64 data
          try {
            // Prevent duplicate downloads by checking if this file was already downloaded
            const downloadId = `${parsedMessage.data.filename}_${parsedMessage.data.count}`;
            const downloadKey = `excel_download_${downloadId}`;
            
            // Check if we already downloaded this file in the last 5 seconds
            const lastDownload = sessionStorage.getItem(downloadKey);
            const now = Date.now();
            if (lastDownload && (now - parseInt(lastDownload)) < 5000) {
              console.log('Preventing duplicate download for:', downloadId);
              return;
            }
            
            // Mark this download as processed
            sessionStorage.setItem(downloadKey, now.toString());
            
            // Convert base64 to blob
            const binaryString = atob(parsedMessage.data.data);
            const bytes = new Uint8Array(binaryString.length);
            for (let i = 0; i < binaryString.length; i++) {
              bytes[i] = binaryString.charCodeAt(i);
            }
            const blob = new Blob([bytes], { 
              type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
            });
            
            // Create download link and trigger download
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = parsedMessage.data.filename || 'export.xlsx';
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
            
            setExportStatus({
              open: true,
              message: t('export_to_excel.success', `Export successful (${parsedMessage.data.count || 0} records)`),
              severity: 'success'
            });
          } catch (error) {
            console.error('Error processing Excel download:', error);
            setExportStatus({
              open: true,
              message: t('export_to_excel.download_error', 'Failed to download the Excel file'),
              severity: 'error'
            });
          }
        } else if (parsedMessage.data && parsedMessage.data.status === 'success') {
          // Legacy success message (should not happen with new implementation)
          setExportStatus({
            open: true,
            message: t('export_to_excel.success', 'Export successful'),
            severity: 'success'
          });
        } else if (parsedMessage.data && parsedMessage.data.status === 'error') {
          setExportStatus({
            open: true,
            message: parsedMessage.data.message || t('export_to_excel.error', 'Export failed'),
            severity: 'error'
          });
        }
      }
    });

    return unsubscribe;
  }, [subscribeToMessages, t]);

  // Handle closing the status alert
  const handleCloseAlert = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setExportStatus(prev => ({ ...prev, open: false }));
  };

  const handleOpenTagDialog = useCallback(() => {
    if (!data || !Array.isArray(data) || data.length === 0) {
      console.error('Invalid data');
      return;
    }

    setTagFilter('');
    setTagDialogOpen(true);
  }, [data]);

  const handleCloseTagDialog = useCallback(() => {
    if (isExporting) {
      return;
    }
    setTagDialogOpen(false);
  }, [isExporting]);

  const handleToggleTag = useCallback((tagName) => {
    setSelectionTouched(true);
    setSelectedTags((current) => (
      current.includes(tagName)
        ? current.filter((item) => item !== tagName)
        : [...current, tagName]
    ));
  }, []);

  const handleSelectAllTags = useCallback(() => {
    setSelectionTouched(true);
    setSelectedTags(availableTagNames);
  }, [availableTagNames]);

  const handleClearTags = useCallback(() => {
    setSelectionTouched(true);
    setSelectedTags([]);
  }, []);

  // Function to export data to Excel
  const exportToExcel = useCallback((selectedTagNames = selectedTags) => {
    if (!data || !Array.isArray(data) || data.length === 0) {
      console.error('Invalid data');
      return;
    }

    if (!ws || ws.readyState !== WebSocket.OPEN) {
      console.error('WebSocket connection is not available');
      return;
    }

    setIsExporting(true);
    ws.send(JSON.stringify({
      command: 'exportexcel',
      caseid: caseid,
      columns: selectedTagNames,
    }));
    setTagDialogOpen(false);
  }, [data, ws, caseid, selectedTags]);

  return (
    <>
      <Tooltip title={t('export_to_excel.tooltip')}>
        <IconButton 
          onClick={handleOpenTagDialog} 
          disabled={isExporting}
          sx={{
            position: 'relative',
            '&.Mui-disabled': {
              opacity: 0.7,
              filter: 'grayscale(30%)'
            }
          }}
        >
          <img 
            src={excelExportIcon} 
            alt={t('export_to_excel.alt_text')} 
            style={{ width: 24, height: 24 }} 
          />
          {isExporting && (
            <CircularProgress 
              size={28} 
              sx={{ 
                position: 'absolute', 
                color: primaryColor,
                opacity: 0.8
              }} 
            />
          )}
          </IconButton>
      </Tooltip>

      <Dialog
        open={tagDialogOpen}
        onClose={handleCloseTagDialog}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle sx={{ pb: 1 }}>
          {t('export_to_excel.select_columns_title', 'Choose columns to export')}
        </DialogTitle>
        <DialogContent dividers sx={{ pt: 2 }}>
          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
            {t(
              'export_to_excel.select_columns_description',
              'Select the columns you want included in the Excel file.'
            )}
          </Typography>

          <TextField
            fullWidth
            size="small"
            value={tagFilter}
            onChange={(event) => setTagFilter(event.target.value)}
            placeholder={t('export_to_excel.column_filter_placeholder', 'Filter columns')}
            sx={{ mb: 2 }}
          />

          <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap' }}>
            <Button size="small" variant="outlined" onClick={handleSelectAllTags}>
              {t('export_to_excel.select_all_columns', 'Select all')}
            </Button>
            <Button size="small" variant="outlined" onClick={handleClearTags}>
              {t('export_to_excel.clear_all_columns', 'Clear all')}
            </Button>
            <Chip
              size="small"
              label={t('export_to_excel.selected_columns_count', {
                count: selectedTags.length,
                defaultValue: '{{count}} selected',
              })}
              sx={{ ml: 'auto' }}
            />
          </Stack>

          {selectedTags.length === 0 && (
            <Typography variant="caption" sx={{ display: 'block', color: 'error.main', mb: 1 }}>
              {t(
                'export_to_excel.select_at_least_one_column',
                'Select at least one column to export.'
              )}
            </Typography>
          )}

          <Box
            sx={{
              maxHeight: 360,
              overflowY: 'auto',
              border: '1px solid rgba(87, 87, 176, 0.16)',
              borderRadius: 2,
              p: 1,
              backgroundColor: '#fff',
            }}
          >
            {filteredTags.length > 0 ? (
              filteredTags.map((tag) => (
                <FormControlLabel
                  key={tag.variable}
                  control={(
                    <Checkbox
                      checked={selectedTags.includes(tag.variable)}
                      onChange={() => handleToggleTag(tag.variable)}
                    />
                  )}
                  label={(
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {tag.label}
                      </Typography>
                      {tag.variable !== tag.label && (
                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                          {tag.variable}
                        </Typography>
                      )}
                    </Box>
                  )}
                  sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    width: '100%',
                    m: 0,
                    py: 0.5,
                    px: 0.5,
                    borderRadius: 1,
                    '&:hover': {
                      backgroundColor: 'rgba(87, 87, 176, 0.04)',
                    },
                  }}
                />
              ))
            ) : (
              <Typography variant="body2" sx={{ color: 'text.secondary', p: 1 }}>
                {availableTags.length === 0
                  ? t('export_to_excel.no_columns_available', 'No columns found for this export.')
                  : t('export_to_excel.no_columns_match_filter', 'No columns match the current filter.')}
              </Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={handleCloseTagDialog} disabled={isExporting}>
            {t('cancel', 'Cancel')}
          </Button>
          <Button
            onClick={() => exportToExcel()}
            variant="contained"
            disabled={isExporting || selectedTags.length === 0}
          >
            {t('export_to_excel.export_selected_columns', 'Export selected columns')}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Loading indicator shown at the bottom of the screen */}
      <Snackbar 
        open={isExporting} 
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        TransitionComponent={Slide}
        sx={{ bottom: exportStatus.open ? '80px' : '20px' }}
      >
        <Alert 
          icon={<CircularProgress size={20} sx={{ color: primaryColor }} />}
          severity="info" 
          sx={{ 
            width: '100%', 
            backgroundColor: lightPurple, 
            color: primaryColor,
            border: `1px solid ${primaryColor}`,
            borderRadius: '12px',
            boxShadow: '0 4px 12px rgba(87, 87, 176, 0.15)',
            display: 'flex',
            alignItems: 'center',
            '& .MuiAlert-icon': {
              color: primaryColor,
              marginRight: '12px',
              padding: '0'
            }
          }}
        >
          <Typography variant="body1" sx={{ fontWeight: 500 }}>
            {t('export_to_excel.exporting', 'Exporting data to Excel...')}
          </Typography>
        </Alert>
      </Snackbar>
      
      {/* Success/Error notification */}
      <Snackbar 
        open={exportStatus.open} 
        autoHideDuration={6000} 
        onClose={handleCloseAlert}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        TransitionComponent={Slide}
      >
        <Alert 
          onClose={handleCloseAlert} 
          severity={exportStatus.severity} 
          sx={{ 
            width: '100%',
            backgroundColor: exportStatus.severity === 'success' ? '#e8f5e9' : (exportStatus.severity === 'error' ? '#ffebee' : lightPurple),
            color: exportStatus.severity === 'success' ? '#2e7d32' : (exportStatus.severity === 'error' ? '#d32f2f' : primaryColor),
            border: `1px solid ${exportStatus.severity === 'success' ? '#a5d6a7' : (exportStatus.severity === 'error' ? '#ef9a9a' : primaryColor)}`,
            borderRadius: '12px',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
            '& .MuiAlert-icon': {
              color: exportStatus.severity === 'success' ? '#2e7d32' : (exportStatus.severity === 'error' ? '#d32f2f' : primaryColor)
            }
          }}
        >
          {exportStatus.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default ExportToExcel;
